import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuthStore } from './store/authStore';
import { useThemeStore } from './store/themeStore';
import { AuthGuard } from './components/guards';
import { Navigation, ActingForBanner } from './components/layout';
import { ActivityDetailPanel } from './components/activities';
import { CalendarReconnectBanner } from './components/gmail/CalendarReconnectBanner';
// Conditionally import + register dev login. The static `if` lets Vite
// tree-shake DevLogin.tsx out of the production bundle when the flag
// is 'false' at build time.
import { DevLogin } from './pages/DevLogin';
import {
  SignIn,
  Dashboard,
  Organizations,
  OrganizationDetail,
  People,
  PersonDetail,
  Tasks,
  Activities,
  Settings,
  Competitors,
  TerritoryMap,
  TerritoryAdmin,
  TerritoryAnalyze,
  ImportManagement,
  ImportReview,
  ImportSummary,
  MatchingReview,
  BlastRedirect,
  TeamDashboard,
  Lists,
  ListDetail,
  Explore,
  Requests,
  CoachFlagsPage,
  CoachChangeReports,
  PublicLeadForm,
  LeadQueue,
  ReviewPage,
  Campaigns,
  CampaignComposer,
  EmailTemplates,
} from './pages';
import {
  AdminLayout,
  OverviewTab,
  SyncJobsTab,
  BackupsTab,
  AuditTimelineSubTab,
  AuditByUserSubTab,
  AuditByEntitySubTab,
  SportSeasonsTab,
  RegionsTab,
} from './pages/admin';
import { CompetitorScrapingTab } from './components/admin';
import { MinicrmMigrationPage } from './pages/admin/MinicrmMigrationPage';
import { AdminUsers } from './pages/admin/AdminUsers';
import { SalesTeamsTab } from './pages/admin/SalesTeamsTab';
import { CallQueues } from './pages/CallQueues';
import { CallQueueDetail } from './pages/CallQueueDetail';
import { CallQueueWork } from './pages/CallQueueWork';
import { GlobalSearch } from './components/search';
import { useSearchStore } from './store/searchStore';
import { useFavoriteStore } from './store/favoriteStore';

const enableDevLogin = import.meta.env.VITE_ENABLE_DEV_LOGIN === 'true';
// CRM-35: Call Queues are hidden behind a build-time flag until the feature is
// ready for prime time. Off everywhere by default.
const enableCallQueues = import.meta.env.VITE_ENABLE_CALL_QUEUES === 'true';
const enableCampaigns = import.meta.env.VITE_ENABLE_EMAIL_CAMPAIGNS === 'true';

/**
 * Layout wrapper for authenticated pages
 * Includes navigation bar and main content area
 */
const AuthenticatedLayout = () => {
  const toggleSearch = useSearchStore((s) => s.toggle);

  // Hydrate favorites once on mount (auth is guaranteed here via AuthGuard).
  // Swallow errors so a failed fetch never blocks the rest of the app.
  useEffect(() => {
    useFavoriteStore.getState().hydrate().catch(() => {});
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const cmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (cmdK) {
        e.preventDefault();
        toggleSearch();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [toggleSearch]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navigation />
      <ActingForBanner />
      <CalendarReconnectBanner />
      <main>
        <Outlet />
      </main>
      <GlobalSearch />
      <ActivityDetailPanel />
    </div>
  );
};

function App() {
  const { initTheme } = useThemeStore();
  const mode = useThemeStore((s) => s.mode);

  // Restore auth session from localStorage (shared across tabs, survives reloads)
  useEffect(() => {
    useAuthStore.getState().restoreSession();
  }, []);

  // Initialize theme on app load
  useEffect(() => {
    initTheme();
  }, [initTheme]);

  // Derive toast styles from current theme
  const isDark = mode === 'dark' || (mode === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  return (
    <BrowserRouter>
      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: isDark ? '#1f2937' : '#fff',
            color: isDark ? '#f3f4f6' : '#333',
          },
          success: {
            iconTheme: {
              primary: '#85ba12',
              secondary: isDark ? '#1f2937' : '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: isDark ? '#1f2937' : '#fff',
            },
          },
        }}
      />

      <Routes>
        {/* Public unauthenticated landing pages */}
        {enableDevLogin && (
          <Route path="/dev/login" element={<DevLogin />} />
        )}
        <Route path="/sign-in" element={<SignIn />} />

        {/* Public lead capture (CRM-129). /lead/:repId is the link a rep hands out at a
            conference; /lead is the cold form behind the website's request-info link.
            Intentionally outside AuthGuard — a coach filling this in has no CRM account. */}
        <Route path="/lead" element={<PublicLeadForm />} />
        <Route path="/lead/:repId" element={<PublicLeadForm />} />

        {/* Protected routes with authenticated layout */}
        <Route
          element={
            <AuthGuard>
              <AuthenticatedLayout />
            </AuthGuard>
          }
        >
          {/* Dashboard - default landing page */}
          <Route path="/" element={<Dashboard />} />

          {/* Team (manager cockpit) */}
          <Route path="/team" element={<TeamDashboard />} />

          {/* Assignment requests inbox */}
          <Route path="/requests" element={<Requests />} />

          {/* Lead review queue — manager gate for publicly captured pre-leads (CRM-129) */}
          <Route path="/leads" element={<LeadQueue />} />

          {/* Stakeholder review pages — deliberately unlisted: no nav entry, reachable
              only by direct link, for any signed-in user (same pattern as the call queue). */}
          <Route path="/reviews/:slug" element={<ReviewPage />} />

          {/* Coach move flags — steward review queue + inside-sales call list (CRM-110) */}
          <Route path="/coach-flags" element={<CoachFlagsPage />} />

          {/* Coach change reports — the assigned rep's pending heads-up tasks */}
          <Route path="/coach-change-reports" element={<CoachChangeReports />} />

          {/* Organizations */}
          <Route path="/organizations" element={<Organizations />} />
          <Route path="/organizations/:id" element={<OrganizationDetail />} />

          {/* Lists */}
          <Route path="/lists" element={<Lists />} />
          <Route path="/lists/:id" element={<ListDetail />} />

          {/* Explore — Lists v2 filter-table */}
          <Route path="/explore" element={<Explore />} />

          {/* Competitor Intel */}
          <Route path="/competitors" element={<Competitors />} />

          {/* People */}
          <Route path="/people" element={<People />} />
          <Route path="/people/:id" element={<PersonDetail />} />

          {/* Activities */}
          <Route path="/activities" element={<Activities />} />
          <Route path="/tasks" element={<Navigate to="/activities" replace />} />

          {/* Territory Management */}
          <Route path="/territories" element={<TerritoryMap />} />
          <Route path="/territories/admin" element={<TerritoryAdmin />} />
          <Route path="/territories/analyze" element={<TerritoryAnalyze />} />

          {/* Settings */}
          <Route path="/settings" element={<Settings />} />

          {/* Admin */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<OverviewTab />} />
            <Route path="syncs" element={<SyncJobsTab />} />
            <Route path="backups" element={<BackupsTab />} />
            <Route path="audit" element={<Navigate to="/admin/audit/timeline" replace />} />
            <Route path="audit/timeline" element={<AuditTimelineSubTab />} />
            <Route path="audit/by-user" element={<AuditByUserSubTab />} />
            <Route path="audit/by-entity" element={<AuditByEntitySubTab />} />
            <Route path="matching" element={<MatchingReview />} />
            <Route path="imports" element={<ImportManagement />} />
            <Route path="imports/:batchId/review" element={<ImportReview />} />
            <Route path="imports/:batchId/summary" element={<ImportSummary />} />
            <Route path="competitor-scraping" element={<CompetitorScrapingTab />} />
            <Route path="sport-seasons" element={<SportSeasonsTab />} />
            <Route path="regions" element={<RegionsTab />} />
            <Route path="minicrm-migration" element={<MinicrmMigrationPage />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="sales-teams" element={<SalesTeamsTab />} />
          </Route>

          {/* Call Queues (inside-sales) — gated behind VITE_ENABLE_CALL_QUEUES (CRM-35) */}
          {enableCallQueues && (
            <>
              <Route path="/call-queues" element={<CallQueues />} />
              <Route path="/call-queues/:id" element={<CallQueueDetail />} />
              <Route path="/call-queues/:id/work" element={<CallQueueWork />} />
            </>
          )}

          {/* Email campaigns (admin) — gated behind VITE_ENABLE_EMAIL_CAMPAIGNS */}
          {enableCampaigns && (
            <>
              <Route path="/campaigns" element={<Campaigns />} />
              <Route path="/campaigns/templates" element={<EmailTemplates />} />
              <Route path="/campaigns/new" element={<CampaignComposer />} />
              <Route path="/campaigns/:id" element={<CampaignComposer />} />
            </>
          )}

          {/* Blast deep link redirects */}
          <Route path="/blast/school/:blastSchoolId" element={<BlastRedirect type="school" />} />
          <Route path="/blast/club/:blastClubId" element={<BlastRedirect type="club" />} />
          <Route path="/blast/band/:blastBandId" element={<BlastRedirect type="band" />} />
          <Route path="/blast/user/:blastUserId" element={<BlastRedirect type="user" />} />
        </Route>

        {/* Catch-all redirect to dashboard */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
